import React from 'react';
import styled from 'styled-components';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';
import { ChartCard } from './ChartCard';
import { colors } from '../../theme/tokens';
import { DEMO_EXTRACTION_PREVIOUS, DEMO_EXTRACTION_CURRENT } from './demoData';

const DEMO_PREVIOUS = DEMO_EXTRACTION_PREVIOUS;
const DEMO_CURRENT = DEMO_EXTRACTION_CURRENT;

const PIE_COLORS = [colors.indigo, colors.inkFaint];

export const ExtractionDistributionCard: React.FC = () => {
  const prevTotal = DEMO_PREVIOUS.reduce((s, d) => s + d.value, 0);
  const curTotal = DEMO_CURRENT.reduce((s, d) => s + d.value, 0);

  return (
    <ChartCard
      title="Répartition Extraction des Articles"
      subtitle="Comparaison année précédente vs année en cours"
    >
      <Row>
        <HalfCol>
          <YearLabel>2025</YearLabel>
          <ChartWrap>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={DEMO_PREVIOUS}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                >
                  {DEMO_PREVIOUS.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartWrap>
          <StatsRow>
            {DEMO_PREVIOUS.map((item, i) => (
              <StatBox key={item.name} $accent={i === 0}>
                <StatLabel>{item.name}</StatLabel>
                <StatValue>{item.value}</StatValue>
                <StatPct>
                  {((item.value / prevTotal) * 100).toFixed(1)}%
                </StatPct>
              </StatBox>
            ))}
          </StatsRow>
        </HalfCol>

        <HalfCol>
          <YearLabel>2026</YearLabel>
          <ChartWrap>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={DEMO_CURRENT}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                >
                  {DEMO_CURRENT.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartWrap>
          <StatsRow>
            {DEMO_CURRENT.map((item, i) => (
              <StatBox key={item.name} $accent={i === 0}>
                <StatLabel>{item.name}</StatLabel>
                <StatValue>{item.value}</StatValue>
                <StatPct>{((item.value / curTotal) * 100).toFixed(1)}%</StatPct>
              </StatBox>
            ))}
          </StatsRow>
        </HalfCol>
      </Row>
    </ChartCard>
  );
};

// --- Styled ---

const Row = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const HalfCol = styled.div`
  flex: 1;
  min-width: 260px;
`;

const YearLabel = styled.h4`
  font-family: var(--font-sans-semibold, var(--font-sans));
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  text-align: center;
  margin: 0 0 8px;
`;

const ChartWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
`;

const StatBox = styled.div<{ $accent?: boolean }>`
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  border: 1px solid ${(p) => (p.$accent ? colors.indigoSoft : 'var(--line)')};
  background: ${(p) => (p.$accent ? colors.indigoSoft : 'var(--paper)')};
`;

const StatLabel = styled.p`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--ink-muted);
  margin: 0;
`;

const StatValue = styled.p`
  font-family: var(--font-sans-semibold, var(--font-sans));
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  margin: 2px 0 0;
`;

const StatPct = styled.p`
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  color: var(--ink-faint);
  margin: 2px 0 0;
`;
